/**
 * schema.js — JSON-LD builders (schema.org structured data)
 * ──────────────────────────────────────────────────────────
 * Pure data, no browser APIs: imported by the runtime <Seo> component,
 * by scripts/postbuild-seo.mjs (Node), and by netlify/functions/card-page.js
 * (esbuild bundles it in). Keep it dependency-free.
 *
 * Everything hangs off stable @id anchors so Google merges the graph
 * across pages instead of seeing N disconnected Organizations.
 */

export const ORIGIN = 'https://cardpass.co';

export const ORG_ID = `${ORIGIN}/#organization`;
export const SITE_ID = `${ORIGIN}/#website`;
export const APP_ID = `${ORIGIN}/#app`;

export const organization = () => ({
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'CardPass',
  url: `${ORIGIN}/`,
  logo: {
    '@type': 'ImageObject',
    url: `${ORIGIN}/favicon-512.png`,
    width: 512,
    height: 512
  },
  founder: {
    '@type': 'Person',
    name: 'Mohammed Al-Zahrani',
    alternateName: 'محمد الزهراني',
    url: 'https://engmohammed.net'
  },
  sameAs: ['https://apps.apple.com/app/id6813832210'],
  areaServed: ['SA', 'AE', 'KW', 'QA', 'BH', 'OM'],
  knowsLanguage: ['ar', 'en']
});

export const webSite = () => ({
  '@type': 'WebSite',
  '@id': SITE_ID,
  name: 'CardPass',
  alternateName: 'كارد باس',
  url: `${ORIGIN}/`,
  inLanguage: ['ar', 'en'],
  publisher: { '@id': ORG_ID }
});

export const softwareApp = (locale = 'ar') => ({
  '@type': 'SoftwareApplication',
  '@id': APP_ID,
  name: 'CardPass',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'iOS, Android, Web',
  url: `${ORIGIN}/`,
  description:
    locale === 'ar'
      ? 'بطاقة عمل رقمية تعيش في Apple Wallet و Google Wallet: تُنشأ في 30 ثانية، تتحدّث تلقائياً، وتُشارك بمسح QR واحد يحفظ جهة الاتصال مباشرة.'
      : 'A digital business card that lives in Apple Wallet and Google Wallet: created in 30 seconds, auto-updating, shared with one QR scan that saves your contact instantly.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'SAR',
    description: locale === 'ar' ? 'الخطة المجانية — بدون بطاقة ائتمانية' : 'Free plan — no credit card required'
  },
  publisher: { '@id': ORG_ID },
  inLanguage: ['ar', 'en']
});

export const webPage = ({ path, title, description, locale = 'ar' }) => ({
  '@type': 'WebPage',
  '@id': `${ORIGIN}${path}#webpage`,
  url: `${ORIGIN}${path}`,
  name: title,
  description,
  isPartOf: { '@id': SITE_ID },
  inLanguage: locale === 'ar' ? 'ar' : 'en'
});

export const breadcrumbs = (items) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${ORIGIN}${it.path}`
  }))
});

export const faqPage = (faqs, locale = 'ar') => ({
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: typeof f.q === 'string' ? f.q : f.q[locale],
    acceptedAnswer: {
      '@type': 'Answer',
      text: typeof f.a === 'string' ? f.a : f.a[locale]
    }
  }))
});

/** Public card page → Person + ProfilePage (used by the card-page function). */
export const personProfile = ({ slug, name, nameAr, title, company, photoUrl }) => {
  const person = {
    '@type': 'Person',
    name: name || nameAr,
    ...(nameAr && name && nameAr !== name ? { alternateName: nameAr } : {}),
    ...(title ? { jobTitle: title } : {}),
    ...(company ? { worksFor: { '@type': 'Organization', name: company } } : {}),
    ...(photoUrl ? { image: photoUrl } : {}),
    url: `${ORIGIN}/${slug}`
  };
  return {
    '@type': 'ProfilePage',
    '@id': `${ORIGIN}/${slug}#profile`,
    url: `${ORIGIN}/${slug}`,
    isPartOf: { '@id': SITE_ID },
    mainEntity: person
  };
};

/** Wrap page-specific nodes with the site-wide anchors into one @graph script. */
export const graph = (...nodes) => ({
  '@context': 'https://schema.org',
  '@graph': [organization(), webSite(), ...nodes.filter(Boolean)]
});
